import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const TenantSettingsCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: s } = useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: () => api.get('/tenant-settings', { params: tenantId ? { tenant_id: tenantId } : {} }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const [form, setForm] = useState({ attendance_threshold: 75, sibling_discount_pct: 0, late_fee_pct: 0, late_fee_grace_days: 7 });
  useEffect(() => { if (s) setForm(s); }, [s]);
  const saveMut = useMutation({
    mutationFn: () => api.patch('/tenant-settings', { tenant_id: tenantId, attendance_threshold: parseInt(form.attendance_threshold, 10) || 0, sibling_discount_pct: parseFloat(form.sibling_discount_pct) || 0, late_fee_pct: parseFloat(form.late_fee_pct) || 0, late_fee_grace_days: parseInt(form.late_fee_grace_days, 10) || 0 }),
    onSuccess: () => { toast.success('Alert & discount settings saved'); qc.invalidateQueries({ queryKey: ['tenant-settings'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="tenant-settings-panel">
      <h2 className="font-display text-lg font-semibold mb-1">Alerts & discounts</h2>
      <p className="text-sm text-slate-500 mb-4">Guardians are warned when 30-day attendance drops below the threshold (max once a week). Sibling discount auto-applies as a concession when new invoices are generated for families with 2+ enrolled children.</p>
      <div className="flex gap-6 flex-wrap items-end">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Attendance alert threshold (%)</p>
          <Input data-testid="attendance-threshold-input" type="number" className="w-32" value={form.attendance_threshold} onChange={(e) => setForm({ ...form, attendance_threshold: e.target.value })} />
        </div>
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Sibling discount (%, 0 = off)</p>
          <Input data-testid="sibling-discount-input" type="number" className="w-32" value={form.sibling_discount_pct} onChange={(e) => setForm({ ...form, sibling_discount_pct: e.target.value })} />
        </div>
        <div className="flex items-center gap-2 flex-wrap py-2">
          <span className="text-sm w-64">Late fee % (monthly, on overdue balance)</span>
          <Input data-testid="late-fee-pct-input" type="number" className="w-32" value={form.late_fee_pct} onChange={(e) => setForm({ ...form, late_fee_pct: e.target.value })} />
          <span className="text-xs text-slate-400">0 = finance automation off</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap py-2">
          <span className="text-sm w-64">Late fee grace period (days)</span>
          <Input data-testid="late-fee-grace-input" type="number" className="w-32" value={form.late_fee_grace_days} onChange={(e) => setForm({ ...form, late_fee_grace_days: e.target.value })} />
        </div>
        <Button data-testid="tenant-settings-save" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>Save</Button>
      </div>
    </div>
  );
};
